/** Inventory by location: stock levels (edit per location), locations, transfers between locations. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Empty, Form, Input, InputNumber, Modal, Popconfirm, Result, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { fmtDate, listOf, toastErr, totalOf, useSite } from './common';
import { CountrySelect } from './ShopCommon';
import ShopVariantPicker from './ShopVariantPicker';

const { Text } = Typography;

const Levels = ({ locations, reloadLocations }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [f, setF] = useState({ q: '', page: 1 });
    const [data, setData] = useState(null);
    const [edits, setEdits] = useState({}); // "vid:lid" → qty
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        try { const d = await api.get('/inventory', { q: f.q || undefined, page: f.page, per_page: 50 }); setData({ items: listOf(d), total: totalOf(d) }); } catch (e) { toastErr(e); setData({ items: [], total: 0 }); }
    }, [api, f]);
    useEffect(() => { load(); setEdits({}); }, [load]);
    const save = async () => {
        setSaving(true);
        try {
            await api.put('/inventory', { items: Object.entries(edits).map(([k, quantity]) => { const [variant_id, location_id] = k.split(':').map(Number); return { variant_id, location_id, quantity }; }) });
            message.success(t('Saved'));
            setEdits({});
            load();
            reloadLocations();
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };
    const active = locations.filter((l) => l.active);
    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Input.Search allowClear placeholder={t('Search title or SKU')} style={{ width: 260 }} onSearch={(q) => setF({ q, page: 1 })} />
                {Object.keys(edits).length > 0 && <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>{t('Save {{n}} changes', { n: Object.keys(edits).length })}</Button>}
            </Space>
            <Table rowKey="variant_id" size="small" loading={!data} dataSource={data?.items} scroll={{ x: 360 + active.length * 120 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No products track inventory')} /> }}
                pagination={{ current: f.page, pageSize: 50, total: data?.total, hideOnSinglePage: true, onChange: (page) => setF({ ...f, page }) }}
                columns={[
                    { title: t('Product', { context: 'shop' }), key: 'p', render: (_, v) => <div><Text>{v.product_title}</Text>{v.variant_title && v.variant_title !== 'Default' && <Text type="secondary"> · {v.variant_title}</Text>}<div><Text type="secondary" style={{ fontSize: 12 }}>{v.sku}</Text></div></div> },
                    { title: t('Total', { context: 'shop' }), dataIndex: 'available', width: 80, align: 'right' },
                    ...active.map((l) => ({
                        title: l.name, key: `l${l.id}`, width: 120, render: (_, v) => {
                            const k = `${v.variant_id}:${l.id}`;
                            const cur = v.locations?.[l.id] ?? v.locations?.[String(l.id)] ?? 0;
                            return <InputNumber size="small" value={k in edits ? edits[k] : cur} style={{ width: 100, background: k in edits ? '#fffbe6' : undefined }}
                                onChange={(x) => setEdits((e) => { const n = { ...e }; if (x === cur || x === null) delete n[k]; else n[k] = x; return n; })} />;
                        },
                    })),
                ]} />
        </>
    );
};

const LocationModal = ({ loc, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [form] = Form.useForm();
    useEffect(() => { if (loc) form.setFieldsValue({ name: loc.name || '', active: loc.active !== false, is_default: !!loc.is_default, ...(loc.address || {}) }); }, [loc, form]);
    const submit = async () => {
        const v = await form.validateFields();
        const body = { name: v.name, active: v.active, is_default: v.is_default || undefined, address: { line1: v.line1, line2: v.line2, city: v.city, region: v.region, postal_code: v.postal_code, country: v.country, phone: v.phone } };
        try { if (loc.id) await api.put(`/locations/${loc.id}`, body); else await api.post('/locations', body); onSaved(); } catch (e) { toastErr(e); }
    };
    return (
        <Modal open={!!loc} title={loc?.id ? t('Edit location') : t('Add location')} onCancel={onClose} onOk={submit} destroyOnHidden>
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item name="line1" label={t('Address')}><Input /></Form.Item>
                <Form.Item name="line2"><Input placeholder={t('Apartment, suite, etc.')} /></Form.Item>
                <Space wrap>
                    <Form.Item name="city" label={t('City')}><Input /></Form.Item>
                    <Form.Item name="region" label={t('Region')}><Input style={{ width: 120 }} /></Form.Item>
                    <Form.Item name="postal_code" label={t('Postal code')}><Input style={{ width: 120 }} /></Form.Item>
                </Space>
                <Space wrap>
                    <Form.Item name="country" label={t('Country')}><CountrySelect style={{ width: 220 }} /></Form.Item>
                    <Form.Item name="phone" label={t('Phone')}><Input /></Form.Item>
                </Space>
                <Space>
                    <Form.Item name="active" valuePropName="checked" label={t('Active')}><Switch /></Form.Item>
                    {!loc?.is_default && <Form.Item name="is_default" valuePropName="checked" label={t('Default location')}><Checkbox /></Form.Item>}
                </Space>
            </Form>
        </Modal>
    );
};

const Transfers = ({ locations, reloadLocations }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const [picker, setPicker] = useState(false);
    const [tr, setTr] = useState({ from: null, to: null, items: [], note: '', receive_now: false });
    const load = useCallback(() => { api.get('/transfers').then((d) => setRows(listOf(d))).catch((e) => { toastErr(e); setRows([]); }); }, [api]);
    useEffect(() => { load(); }, [load]);
    const name = (id) => locations.find((l) => l.id === id)?.name || `#${id}`;
    const create = async () => {
        try {
            await api.post('/transfers', { from_location_id: tr.from, to_location_id: tr.to, note: tr.note, receive_now: tr.receive_now, items: tr.items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })) });
            message.success(t('Saved'));
            setOpen(false);
            load(); reloadLocations();
        } catch (e) { toastErr(e); }
    };
    const act = async (x, kind) => { try { await api.post(`/transfers/${x.id}/${kind}`); load(); reloadLocations(); } catch (e) { toastErr(e); } };
    const opts = locations.map((l) => ({ value: l.id, label: l.name }));
    return (
        <>
            <Button icon={<PlusOutlined />} style={{ marginBottom: 12 }} disabled={locations.length < 2} onClick={() => { setTr({ from: locations[0]?.id, to: locations[1]?.id, items: [], note: '', receive_now: false }); setOpen(true); }}>{t('New transfer', { context: 'inventory' })}</Button>
            <Table rowKey="id" size="small" loading={!rows} dataSource={rows || []} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 640 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={locations.length < 2 ? t('Add a second location first') : t('No transfers')} /> }}
                columns={[
                    { title: '#', dataIndex: 'id', width: 60 },
                    { title: t('From → to'), key: 'r', render: (_, x) => `${name(x.from_location_id)} → ${name(x.to_location_id)}` },
                    { title: t('Units'), key: 'u', width: 80, render: (_, x) => listOf(x.items).reduce((a, i) => a + (i.quantity || 0), 0) },
                    { title: t('Status'), dataIndex: 'status', width: 120, render: (v) => <Tag color={{ in_transit: 'blue', received: 'green', cancelled: 'default' }[v]}>{t({ in_transit: 'In transit', received: 'Received', cancelled: 'Cancelled' }[v] || v)}</Tag> },
                    { title: t('Created'), dataIndex: 'created_at', width: 160, render: (v) => fmtDate(v) },
                    {
                        title: '', key: 'a', width: 170, render: (_, x) => (x.status === 'in_transit' ? (
                            <Space size={4}><Button size="small" type="primary" onClick={() => act(x, 'receive')}>{t('Receive')}</Button><Popconfirm title={t('Cancel transfer?')} onConfirm={() => act(x, 'cancel')}><Button size="small">{t('Cancel')}</Button></Popconfirm></Space>
                        ) : null),
                    },
                ]} />
            <Modal open={open} title={t('New transfer', { context: 'inventory' })} onCancel={() => setOpen(false)} onOk={create} okButtonProps={{ disabled: !tr.items.length || tr.from === tr.to }} width={600}>
                <Space wrap style={{ marginBottom: 12 }}>
                    <Select value={tr.from} onChange={(from) => setTr({ ...tr, from })} options={opts} style={{ width: 200 }} />
                    <Text>→</Text>
                    <Select value={tr.to} onChange={(to) => setTr({ ...tr, to })} options={opts} style={{ width: 200 }} />
                </Space>
                <Table rowKey="variant_id" size="small" pagination={false} dataSource={tr.items} locale={{ emptyText: t('No items') }}
                    columns={[
                        { title: t('Item', { context: 'shop' }), key: 'i', render: (_, i) => `${i.title}${i.variant_title ? ` · ${i.variant_title}` : ''}` },
                        { title: t('Qty', { context: 'shop' }), key: 'q', width: 100, render: (_, i) => <InputNumber size="small" min={1} value={i.quantity} onChange={(q) => setTr({ ...tr, items: tr.items.map((x) => (x.variant_id === i.variant_id ? { ...x, quantity: q || 1 } : x)) })} /> },
                        { title: '', key: 'd', width: 40, render: (_, i) => <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setTr({ ...tr, items: tr.items.filter((x) => x.variant_id !== i.variant_id) })} /> },
                    ]} />
                <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setPicker(true)}>{t('Add products')}</Button>
                <Input style={{ marginTop: 12 }} placeholder={t('Note', { context: 'shop' })} value={tr.note} onChange={(e) => setTr({ ...tr, note: e.target.value })} />
                <Checkbox style={{ marginTop: 12 }} checked={tr.receive_now} onChange={(e) => setTr({ ...tr, receive_now: e.target.checked })}>{t('Mark as received now')}</Checkbox>
            </Modal>
            <ShopVariantPicker open={picker} onClose={() => setPicker(false)} onPick={(it) => setTr((s) => ({ ...s, items: s.items.some((x) => x.variant_id === it.variant_id) ? s.items : [...s.items, it] }))} />
        </>
    );
};

const ShopInventory = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [locations, setLocations] = useState(null);
    const [soon, setSoon] = useState(false);
    const [edit, setEdit] = useState(null);
    const [tab, setTab] = useState('levels');
    const load = useCallback(() => {
        api.get('/locations', null, QUIET).then((d) => setLocations(listOf(d))).catch((e) => { if (e?.status === 404) setSoon(true); else toastErr(e); setLocations([]); });
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const del = async (l) => { try { await api.del(`/locations/${l.id}`); load(); } catch (e) { toastErr(e); } };
    const locs = useMemo(() => locations || [], [locations]);

    if (soon) return <Card><Result status="info" title={t('Inventory locations are coming soon')} /></Card>;
    return (
        <Card size="small" title={t('Inventory')}>
            <Tabs activeKey={tab} onChange={setTab} destroyInactiveTabPane items={[
                { key: 'levels', label: t('Stock'), children: <Levels locations={locs} reloadLocations={load} /> },
                {
                    key: 'locations', label: t('Locations'), children: (
                        <>
                            <Button icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => setEdit({ active: true })}>{t('Add location')}</Button>
                            <Table rowKey="id" size="small" loading={!locations} dataSource={locs} pagination={false} scroll={{ x: 560 }}
                                columns={[
                                    { title: t('Name'), key: 'n', render: (_, l) => <Space>{l.name}{l.is_default && <Tag color="blue">{t('Default')}</Tag>}{!l.active && <Tag>{t('Inactive')}</Tag>}</Space> },
                                    { title: t('Address'), key: 'a', render: (_, l) => <Text type="secondary">{[l.address?.line1, l.address?.city, l.address?.country].filter(Boolean).join(', ') || '-'}</Text> },
                                    { title: t('Units'), dataIndex: 'units', width: 90, align: 'right' },
                                    {
                                        title: '', key: 'x', width: 90, render: (_, l) => (
                                            <Space size={0}>
                                                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEdit(l)} />
                                                {!l.is_default && <Popconfirm title={t('Delete this location?')} onConfirm={() => del(l)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}
                                            </Space>
                                        ),
                                    },
                                ]} />
                        </>
                    ),
                },
                { key: 'transfers', label: t('Transfers'), children: <Transfers locations={locs} reloadLocations={load} /> },
            ]} />
            <LocationModal loc={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
        </Card>
    );
};

export default ShopInventory;
